import { useEffect, useMemo, useState } from 'react';
import { Copy, KeyRound, RefreshCw, ShieldOff, UserCheck, UserPlus, Users } from 'lucide-react';
import type { Player } from './types';
import { getSupabase } from './supabase';

type PlayerAccount = {
  user_id: string;
  player_id: string;
  role: 'player';
  email: string | null;
};

type CredentialResult = {
  playerName: string;
  email: string;
  password: string;
};

async function callAccessFunction(body: Record<string, unknown>) {
  const client = getSupabase();
  const { data, error } = await client.functions.invoke('manage-player-access', { body });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data;
}

export function PlayerAccessManager({
  teamId,
  players,
}: {
  teamId: string;
  players: Player[];
}) {
  const [accounts, setAccounts] = useState<PlayerAccount[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [busyPlayerId, setBusyPlayerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [credentials, setCredentials] = useState<CredentialResult | null>(null);

  const accountByPlayer = useMemo(
    () => new Map(accounts.map((account) => [account.player_id, account])),
    [accounts],
  );

  const orderedPlayers = useMemo(
    () => [...players].sort((a, b) => {
      const aNumber = a.squad_number ?? 999;
      const bNumber = b.squad_number ?? 999;
      return aNumber - bNumber || a.name.localeCompare(b.name);
    }),
    [players],
  );

  async function refreshAccounts() {
    if (!teamId) return;
    setLoading(true);
    try {
      const data = await callAccessFunction({ action: 'list', team_id: teamId });
      const next = (data.accounts || []) as PlayerAccount[];
      setAccounts(next);
      setEmails((current) => {
        const merged = { ...current };
        next.forEach((account) => {
          if (account.email && !merged[account.player_id]) merged[account.player_id] = account.email;
        });
        return merged;
      });
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to load player access.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshAccounts();
  }, [teamId]);

  async function createLogin(player: Player) {
    const email = (emails[player.id] || '').trim();
    if (!email) {
      setMessage(`Enter an email for ${player.name}.`);
      return;
    }

    setBusyPlayerId(player.id);
    setCredentials(null);
    try {
      const data = await callAccessFunction({
        action: 'create',
        team_id: teamId,
        player_id: player.id,
        email,
      });

      setCredentials({
        playerName: player.name,
        email: String(data.account?.email || email),
        password: String(data.temporary_password || ''),
      });
      setMessage(`${player.name}: player login ready.`);
      await refreshAccounts();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not create player login.');
    } finally {
      setBusyPlayerId(null);
    }
  }

  async function resetPassword(player: Player) {
    setBusyPlayerId(player.id);
    setCredentials(null);
    try {
      const data = await callAccessFunction({
        action: 'reset_password',
        team_id: teamId,
        player_id: player.id,
      });
      setCredentials({
        playerName: player.name,
        email: String(data.email || accountByPlayer.get(player.id)?.email || ''),
        password: String(data.temporary_password || ''),
      });
      setMessage(`${player.name}: temporary password reset.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not reset password.');
    } finally {
      setBusyPlayerId(null);
    }
  }

  async function revokeAccess(player: Player) {
    if (!window.confirm(`Revoke app access for ${player.name}?`)) return;
    setBusyPlayerId(player.id);
    setCredentials(null);
    try {
      await callAccessFunction({
        action: 'revoke',
        team_id: teamId,
        player_id: player.id,
      });
      setMessage(`${player.name}: access revoked.`);
      await refreshAccounts();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not revoke access.');
    } finally {
      setBusyPlayerId(null);
    }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setMessage('Copied to clipboard.');
    } catch {
      setMessage('Copy failed. Press and hold the value to copy.');
    }
  }

  return (
    <section className="metal-card player-access-manager">
      <div className="player-access-head">
        <div>
          <p className="eyebrow">PLAYER ACCESS · ADMIN ONLY</p>
          <h2>Player Login Manager</h2>
          <p className="muted">
            Create one read-only app login per player. The account is linked automatically to the correct player profile.
          </p>
        </div>
        <button className="steel-button" type="button" onClick={() => void refreshAccounts()} disabled={loading}>
          <RefreshCw size={16} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {message && <div className="player-access-message">{message}</div>}

      {credentials && (
        <div className="player-credentials-card">
          <div>
            <p className="eyebrow">TEMPORARY LOGIN</p>
            <h3>{credentials.playerName}</h3>
            <p>Send these credentials privately. The player can change the password after logging in.</p>
          </div>
          <div className="credential-line">
            <span>Email</span><strong>{credentials.email}</strong>
            <button type="button" onClick={() => void copy(credentials.email)}><Copy size={15}/></button>
          </div>
          <div className="credential-line">
            <span>Password</span><strong>{credentials.password}</strong>
            <button type="button" onClick={() => void copy(credentials.password)}><Copy size={15}/></button>
          </div>
          <button className="steel-button" type="button" onClick={() => setCredentials(null)}>Hide credentials</button>
        </div>
      )}

      <div className="player-access-summary">
        <span><Users size={16}/> {players.length} squad players</span>
        <span><UserCheck size={16}/> {accounts.length} active player logins</span>
        <span>{Math.max(0, players.length - accounts.length)} remaining</span>
      </div>

      <div className="player-access-list">
        {orderedPlayers.map((player) => {
          const account = accountByPlayer.get(player.id);
          const busy = busyPlayerId === player.id;
          return (
            <article className={`player-access-row ${account ? 'linked' : ''}`} key={player.id}>
              <div className="player-access-identity">
                <span className="player-access-number">{player.squad_number ?? '—'}</span>
                <div>
                  <strong>{player.name}</strong>
                  <small>{player.position || 'TBA'}</small>
                </div>
                <span className={`player-access-status ${account ? 'active' : 'pending'}`}>
                  {account ? 'LOGIN ACTIVE' : 'NO LOGIN'}
                </span>
              </div>

              <div className="player-access-controls">
                <input
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="player@email.com"
                  value={emails[player.id] ?? account?.email ?? ''}
                  disabled={Boolean(account) || busy}
                  onChange={(event) => setEmails((current) => ({ ...current, [player.id]: event.target.value }))}
                />

                {!account ? (
                  <button className="gold-button" type="button" disabled={busy} onClick={() => void createLogin(player)}>
                    <UserPlus size={16}/> {busy ? 'Creating…' : 'Create Login'}
                  </button>
                ) : (
                  <div className="player-access-actions">
                    <button className="steel-button" type="button" disabled={busy} onClick={() => void resetPassword(player)}>
                      <KeyRound size={15}/> Reset Password
                    </button>
                    <button className="danger-button compact" type="button" disabled={busy} onClick={() => void revokeAccess(player)}>
                      <ShieldOff size={15}/> Revoke
                    </button>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
